import LocalStorageUtils from './util';
import { ListDisplayType } from '../../containers/DriveItemList/constants';
import { LocalStorageDownloadInfos } from '../../containers/DownloadManager/actions';
import { GalleryInfosState } from '../../containers/Gallery/reducer';
import { DriveListState } from '../../containers/DriveItemList/reducer';
import { OfflineManagerState } from '../../containers/OfflineManager/reducer';
import { AuthenticationUserState } from '../../containers/Authentication/reducer';
import { MultiAppAuthenticationCredentials } from '../../containers/MultiAppAuthentication/reducer';
import { Server } from '../../containers/MultiAppAuthentication/constants';
import { Api } from '../../typings/api';
import { TrackingData } from '../statistics/typings';
import { PendingTasksManagerState } from '../../containers/PendingTasksManager/reducer';
import { LocalStorageNewItemInfos } from '../../containers/DriveItemList/New/actions';
import { LocalStorageReadStatusInfos } from '../../containers/ReadStatus/actions';
import {
	DownloadGroupStatus,
	DownloadGroupName,
	SelectedDownloadsManagerItemState
} from '../../containers/DownloadManager/reducer';
import Utils from '../index';

interface StoredDownloadGroups {
	all: DownloadGroupStatus;
	important: DownloadGroupStatus;
	selected: DownloadGroupStatus;
}

export default class LocalStorageManager extends LocalStorageUtils {
	private static displayTypeKey = 'gallApp-driveItemListDisplayType';
	private static foldersDisplayTypeKey = 'gallApp-foldersDisplayType';
	private static downloadPopupDontAskAgainKey = 'gallApp-downloadPopupDontAskAgain';
	private static selectionKey = 'gallApp-favorites';
	private static notesSelectionKey = 'gallApp-selectedNotes';
	private static downloadsKey = 'gallApp-downloads';
	private static downloadDownloadGroupsKey = 'gallApp-downloadGroups';
	private static newItemsKey = 'gallApp-newItems';
	private static readStatusKey = 'gallApp-readStatus';
	private static storeGalleryConfigKey = 'gallApp-galleryConfig';
	private static storeGalleryInfosKey = 'gallApp-galleryInfos';
	private static storeDriveListKey = 'gallApp-driveList';
	private static storeOfflineManagerKey = 'gallApp-offlineManager';
	private static storeUserInfosKey = 'gallApp-userInfos';
	private static storeProfileInfosKey = 'gallApp-profileInfos';
	private static multiAppCredentialsKey = 'gallApp-multiAppCredentials';
	private static multiAppServerKey = 'gallApp-multiAppServer';
	private static trackingQueueKey = 'gallApp-trackingQueue';
	private static pendingTasksKey = 'gallApp-pendingTasks';
	private static userLocaleKey = 'gallApp-userLocale';
	private static pendingSelectedDownloadsKey = 'gallApp-pendingSelectedDownloads';
	// legacy
	private static downloadAllStatusKey = 'gallApp-downloadAllStatus';
	// these keys are used in web version and set by external login page
	private static username = 'username'; // legacy
	private static password = 'password'; // legacy

	public static setGalleryPrefix(int: number, login?: string): void {
		const prefix = Utils.getGalleryPrefix(int, login);
		LocalStorageUtils.setPrefix(prefix);
	}

	public static setProfileInfos(LocalProfileData: Api.ProfileData): void {
		LocalStorageManager.setItem(LocalStorageManager.storeProfileInfosKey, LocalProfileData, false);
	}

	public static getProfileInfos(): Api.ProfileData | null {
		return LocalStorageManager.getItem<Api.ProfileData>(LocalStorageManager.storeProfileInfosKey, false);
	}

	public static removeProfileInfos(): void {
		LocalStorageManager.removeItem(LocalStorageManager.storeProfileInfosKey, false);
	}

	// User Locale
	public static setUserLocale(locale: string, hasPrefix: boolean): void {
		LocalStorageManager.setItem(LocalStorageManager.userLocaleKey, locale, hasPrefix);
	}

	public static getUserLocale(hasPrefix: boolean): string | null {
		return LocalStorageManager.getItem<ListDisplayType>(LocalStorageManager.userLocaleKey, hasPrefix);
	}

	// DisplayType
	public static setDisplayType(displayType: ListDisplayType): void {
		LocalStorageManager.setItem(LocalStorageManager.displayTypeKey, displayType, true);
	}

	public static getDisplayType(): ListDisplayType | null {
		return LocalStorageManager.getItem<ListDisplayType>(LocalStorageManager.displayTypeKey, true);
	}

	// Folders displayType (is set via dropdown inside each folder and has priority over application or backoffice displayType)
	public static setFolderDisplayType(folderId: number | string, displayType: ListDisplayType): void {
		let foldersDisplayType = LocalStorageManager.getFoldersDisplayType();

		if (!foldersDisplayType) {
			foldersDisplayType = {
				[folderId]: displayType,
			};
		} else {
			foldersDisplayType[folderId] = displayType;
		}

		LocalStorageManager.setItem(LocalStorageManager.foldersDisplayTypeKey, foldersDisplayType, true);
	}

	public static getFoldersDisplayType(): { [k: string]: ListDisplayType } | null {
		return LocalStorageManager.getItem<{ [k: string]: ListDisplayType }>(LocalStorageManager.foldersDisplayTypeKey, true);
	}

	public static resetFoldersDisplayType(): void {
		LocalStorageManager.removeItem(LocalStorageManager.foldersDisplayTypeKey, true);
	}

	// DownloadPopupDontAskAgain
	public static setDownloadPopupDontAskAgain(): void {
		LocalStorageManager.setItem(LocalStorageManager.downloadPopupDontAskAgainKey, true, true);
	}

	public static getDownloadPopupDontAskAgain(): true | null {
		return LocalStorageManager.getItem<true>(LocalStorageManager.downloadPopupDontAskAgainKey, true);
	}

	// Selection
	public static setSelection(selection: number[]): void {
		LocalStorageManager.setItem(LocalStorageManager.selectionKey, selection, true);
	}

	public static getSelection(): number[] | null {
		return LocalStorageManager.getItem<number[]>(LocalStorageManager.selectionKey, true);
	}

	// Notes selection
	public static setNotesSelection(selection: number[]): void {
		LocalStorageManager.setItem(LocalStorageManager.notesSelectionKey, selection, true);
	}

	public static getNotesSelection(): number[] | null {
		return LocalStorageManager.getItem<number[]>(LocalStorageManager.notesSelectionKey, true);
	}

	// DownloadGroups
	public static setDownloadGroups(group: DownloadGroupName, status: DownloadGroupStatus) {
		const downloadGroups = LocalStorageManager.getDownloadGroups() || {
			all: DownloadGroupStatus.NotStarted,
			important: DownloadGroupStatus.NotStarted,
			selected: DownloadGroupStatus.NotStarted,
		};
		const oldDownloadAllStatusFormat = LocalStorageManager.getDownloadAllStatus();
		if (oldDownloadAllStatusFormat) {
			// this is to handle the first application start after updating to 3.53
			downloadGroups[DownloadGroupName.ALL] = oldDownloadAllStatusFormat;
			LocalStorageManager.removeDownloadAllStatus();
		}
		downloadGroups[group] = status;
		LocalStorageManager.setItem(LocalStorageManager.downloadDownloadGroupsKey, downloadGroups, true);
	}

	public static getDownloadGroups(): StoredDownloadGroups | null {
		return LocalStorageManager.getItem<StoredDownloadGroups>(LocalStorageManager.downloadDownloadGroupsKey, true);
	}

	// Downloads
	public static setDownloads(downloads: LocalStorageDownloadInfos[]) {
		LocalStorageManager.setItem(LocalStorageManager.downloadsKey, downloads, true);
	}

	public static getDownloads(): LocalStorageDownloadInfos[] | null {
		return LocalStorageManager.getItem<LocalStorageDownloadInfos[]>(LocalStorageManager.downloadsKey, true);
	}

	public static setPendingSelectedDownloads(driveItemIds: SelectedDownloadsManagerItemState[]) {
		LocalStorageManager.setItem(LocalStorageManager.pendingSelectedDownloadsKey, driveItemIds, true);
	}

	public static getPendingSelectedDownloads(): SelectedDownloadsManagerItemState[] | null {
		return LocalStorageManager.getItem<SelectedDownloadsManagerItemState[]>(LocalStorageManager.pendingSelectedDownloadsKey, true);
	}

	// New Items
	public static setNewItems(newItems: LocalStorageNewItemInfos[]) {
		LocalStorageManager.setItem(LocalStorageManager.newItemsKey, newItems, true);
	}

	public static getNewItemsStore(): LocalStorageNewItemInfos[] | null {
		return LocalStorageManager.getItem<LocalStorageNewItemInfos[]>(LocalStorageManager.newItemsKey, true);
	}

	// Read Status
	public static setReadItems(readItems: LocalStorageReadStatusInfos[]) {
		LocalStorageManager.setItem(LocalStorageManager.readStatusKey, readItems, true);
	}

	public static getReadStatusStore(): LocalStorageReadStatusInfos[] | null {
		return LocalStorageManager.getItem<LocalStorageReadStatusInfos[]>(LocalStorageManager.readStatusKey, true);
	}

	// GalleryConfig
	public static setStoreGalleryConfig(storeGalleryConfig: Api.Config): void {
		LocalStorageManager.setItem(LocalStorageManager.storeGalleryConfigKey, storeGalleryConfig, false);
	}

	public static getStoreGalleryConfig(): Api.Config | null {
		return LocalStorageManager.getItem<Api.Config>(LocalStorageManager.storeGalleryConfigKey, false);
	}

	public static removeStoreGalleryConfig(): void {
		LocalStorageManager.removeItem(LocalStorageManager.storeGalleryConfigKey, false);
	}

	// GalleryInfos
	public static setStoreGalleryInfos(storeGalleryInfos: GalleryInfosState): void {
		LocalStorageManager.setItem(LocalStorageManager.storeGalleryInfosKey, storeGalleryInfos, true);
	}

	public static getStoreGalleryInfos(): GalleryInfosState | null {
		return LocalStorageManager.getItem<GalleryInfosState>(LocalStorageManager.storeGalleryInfosKey, true);
	}

	// DriveList
	public static setStoreDriveList(driveList: DriveListState): void {
		LocalStorageManager.setItem(LocalStorageManager.storeDriveListKey, driveList, true);
	}

	public static getStoreDriveList(): DriveListState | null {
		return LocalStorageManager.getItem<DriveListState>(LocalStorageManager.storeDriveListKey, true);
	}

	// OfflineManager
	public static setStoreOfflineManager(offlineManager: OfflineManagerState): void {
		LocalStorageManager.setItem(LocalStorageManager.storeOfflineManagerKey, offlineManager, true);
	}

	public static getStoreOfflineManager(): OfflineManagerState | null {
		return LocalStorageManager.getItem<OfflineManagerState>(LocalStorageManager.storeOfflineManagerKey, true);
	}

	// Credentials
	public static setCredentials(credentials: AuthenticationUserState): void {
		LocalStorageManager.setItem(LocalStorageManager.storeUserInfosKey, credentials, true);
	}

	public static getCredentials(): AuthenticationUserState | null {
		return LocalStorageManager.getItem<AuthenticationUserState>(LocalStorageManager.storeUserInfosKey, true);
	}

	public static removeCredentials(): void {
		LocalStorageManager.removeItem(LocalStorageManager.storeUserInfosKey, true);
	}

	// MultiAppCredentials
	public static setMultiAppCredentials(credentials: MultiAppAuthenticationCredentials): void {
		LocalStorageManager.setItem(LocalStorageManager.multiAppCredentialsKey, credentials, false);
	}

	public static getMultiAppCredentials(): MultiAppAuthenticationCredentials | null {
		return LocalStorageManager.getItem<MultiAppAuthenticationCredentials>(LocalStorageManager.multiAppCredentialsKey, false);
	}

	public static removeMultiAppCredentials(): void {
		LocalStorageManager.removeItem(LocalStorageManager.multiAppCredentialsKey, false);
	}

	// MultiAppServer
	public static setMultiAppServer(server: Server): void {
		LocalStorageManager.setItem(LocalStorageManager.multiAppServerKey, server, false);
	}

	public static getMultiAppServer(): Server | null {
		return LocalStorageManager.getItem<Server>(LocalStorageManager.multiAppServerKey, false);
	}

	public static removeMultiAppServer(): void {
		LocalStorageManager.removeItem(LocalStorageManager.multiAppServerKey, false);
	}

	// TrackingQueue
	public static setTrackingQueue(trackingQueue: TrackingData[]): void {
		LocalStorageManager.setItem(LocalStorageManager.trackingQueueKey, trackingQueue, true);
	}

	public static getTrackingQueue(): TrackingData[] | null {
		return LocalStorageManager.getItem<TrackingData[]>(LocalStorageManager.trackingQueueKey, true);
	}

	// PendingTasks
	public static setPendingTasks(pendingTasks: PendingTasksManagerState): void {
		LocalStorageManager.setItem(LocalStorageManager.pendingTasksKey, pendingTasks, true);
	}

	public static getPendingTasks(): PendingTasksManagerState | null {
		return LocalStorageManager.getItem<PendingTasksManagerState>(LocalStorageManager.pendingTasksKey, true);
	}

	// LEGACY

	// DownloadAllStatus
	public static getDownloadAllStatus(): DownloadGroupStatus | null {
		return LocalStorageManager.getItem<DownloadGroupStatus>(LocalStorageManager.downloadAllStatusKey, true);
	}

	public static removeDownloadAllStatus() {
		return LocalStorageManager.removeItem(LocalStorageManager.downloadAllStatusKey, true);
	}

	public static removeUsername(): void {
		return localStorage.removeItem(LocalStorageManager.username);
	}

	public static removePassword(): void {
		return localStorage.removeItem(LocalStorageManager.password);
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/localStorage/manager.ts